import type {
  ConsumeDeviceTransferTokenInput,
  ConsumeDeviceTransferTokenResult,
  CreateDeviceTransferTokenResult,
} from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import { auth, functions } from '../../lib/firebase';

/**
 * デバイス間引継 callable (`createDeviceTransferToken` / `consumeDeviceTransferToken`) の hook 2 件
 * (Sprint BG-DataSettings 8、GAP U-C9)。
 *
 * 旧 v1 `old/apps/user/src/components/DeviceTransferModal.tsx` (L3 踏襲) の callable 呼出部分を
 * v2 hook 化:
 *   - `useDeviceTransferIssue`: 引数なし → token + expiresAt 返却
 *   - `useDeviceTransferConsume`: token 入力 → memberships コピー + claims merge、
 *     成功時に `getIdToken(true)` で ID トークン refresh (Rules が accessibleDistricts を即時参照可能に)
 *
 * issueRecoveryCode / recoverParticipant と同じ state machine (idle / pending / success / error) を採用。
 */

export type IssueDeviceTransferState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'pending'; data: null; error: null }
  | { status: 'success'; data: CreateDeviceTransferTokenResult; error: null }
  | { status: 'error'; data: null; error: Error };

export function useDeviceTransferIssue(): {
  state: IssueDeviceTransferState;
  call: () => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<IssueDeviceTransferState>({
    status: 'idle',
    data: null,
    error: null,
  });

  const call = useCallback(async () => {
    setState({ status: 'pending', data: null, error: null });
    try {
      const fn = httpsCallable<undefined, CreateDeviceTransferTokenResult>(
        functions,
        'createDeviceTransferToken',
      );
      const res = await fn();
      setState({ status: 'success', data: res.data, error: null });
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      let message = raw.message ?? String(e);
      if (code.includes('resource-exhausted')) {
        message = '引継コードの発行回数上限に達しました。1 分後に再試行してください。';
      }
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setState({ status: 'error', data: null, error: err });
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', data: null, error: null });
  }, []);

  return { state, call, reset };
}

export type ConsumeDeviceTransferState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'pending'; data: null; error: null }
  | { status: 'success'; data: ConsumeDeviceTransferTokenResult; error: null }
  | { status: 'error'; data: null; error: Error };

export function useDeviceTransferConsume(): {
  state: ConsumeDeviceTransferState;
  call: (input: ConsumeDeviceTransferTokenInput) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<ConsumeDeviceTransferState>({
    status: 'idle',
    data: null,
    error: null,
  });

  const call = useCallback(async (input: ConsumeDeviceTransferTokenInput) => {
    setState({ status: 'pending', data: null, error: null });
    try {
      const fn = httpsCallable<ConsumeDeviceTransferTokenInput, ConsumeDeviceTransferTokenResult>(
        functions,
        'consumeDeviceTransferToken',
      );
      const res = await fn(input);

      // 成功時: Custom Claims 反映済 → ID トークン強制 refresh (recoverParticipant と同方針)
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      setState({ status: 'success', data: res.data, error: null });
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      let message = raw.message ?? String(e);
      if (code.includes('resource-exhausted')) {
        message = '引継コードの試行回数上限に達しました。1 分後に再試行してください。';
      } else if (code.includes('not-found')) {
        message = '引継コードが見つかりません。コードを確認してください。';
      } else if (code.includes('invalid-argument')) {
        message = '引継コードの形式が正しくありません (8 文字)。';
      } else if (code.includes('failed-precondition')) {
        // 期限切れ / 既消費 / 同一 uid → message を decoder
        if (/expired/i.test(message)) {
          message = '引継コードの有効期限が切れました。新しいコードを発行してください。';
        } else if (/already.*used/i.test(message)) {
          message = 'この引継コードは既に使用されています。新しいコードを発行してください。';
        } else if (/same.*user/i.test(message)) {
          message = '同じ端末では引継ぎできません。別端末から発行したコードを入力してください。';
        }
      }
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setState({ status: 'error', data: null, error: err });
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', data: null, error: null });
  }, []);

  return { state, call, reset };
}
